import React, { useEffect, useRef, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  EmailAudienceMemberOption,
  EmailFormState,
  NewsletterMemberFilters,
  RecipientGroup,
  RecipientSourceId,
  buildDefaultNewsletterMemberFilters,
  nonMemberSubscribersRecipientSourceLabel,
} from "./emailMockData";
import { isEmailImageFile } from "./emailAssetUpload";

export type EmailFormMode = "create" | "update";
export type AudienceFilterId = keyof NewsletterMemberFilters;
export type AudienceFilters = NewsletterMemberFilters;

export type UpdateEmailField = <FieldName extends keyof EmailFormState>(
  field: FieldName,
  value: EmailFormState[FieldName]
) => void;

// SES v1 caps the complete base64-encoded message at 10MB. Keeping raw
// attachments at 6MB total leaves room for MIME encoding and the email body.
export const maxTotalAttachmentBytes = 6 * 1024 * 1024;
export const maxAttachmentBytes = maxTotalAttachmentBytes;
export const maxAttachmentCount = 10;
export const allowedAttachmentTypes = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
];
export const attachmentAccept = [
  ".pdf",
  ".doc",
  ".docx",
  "image/*",
].join(",");

export const isAllowedEmailAttachment = ({
  file,
}: {
  file: File;
}): boolean =>
  isEmailImageFile({ file }) || allowedAttachmentTypes.includes(file.type);

export const pageTitleClassName = "text-[36px] font-extrabold leading-tight text-gray-950";
export const pageDescriptionClassName = "mt-3 max-w-6xl text-[18px] leading-7 text-black";
export const stepTitleClassName = "text-[32px] font-extrabold leading-tight text-gray-950";
export const stepDescriptionClassName = "mt-3 max-w-6xl text-[18px] leading-7 text-black";
export const sectionTitleClassName = "text-[26px] font-extrabold leading-tight text-gray-950";
export const sectionDescriptionClassName = "mt-2 text-[16px] leading-6 text-black";
export const fieldLabelClassName = "text-[15px] font-extrabold leading-6 text-gray-950";
export const fieldHelperClassName = "mt-1.5 block text-[14px] leading-5 text-black";
export const inputClassName =
  "h-[48px] w-full rounded-md border border-gray-300 bg-white px-4 text-[15px] text-gray-950 outline-none transition placeholder:text-gray-400 focus:border-cpaGold focus:ring-2 focus:ring-cpaGold/20 disabled:bg-gray-100 disabled:text-gray-500";
export const textareaClassName =
  "min-h-[112px] w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-[15px] text-gray-950 outline-none transition placeholder:text-gray-400 focus:border-cpaGold focus:ring-2 focus:ring-cpaGold/20 disabled:bg-gray-100 disabled:text-gray-500";
export const checkboxClassName =
  "h-5 w-5 shrink-0 rounded border-gray-300 accent-cpaGold";
export const sectionClassName =
  "rounded-md border border-gray-200 bg-white px-8 py-7 shadow-sm";
export const primaryButtonClassName =
  "inline-flex min-h-[46px] items-center justify-center gap-2 rounded-md border border-cpaGold bg-cpaGold px-6 text-[15px] font-extrabold text-white transition hover:bg-cpaGoldDark disabled:cursor-not-allowed disabled:opacity-60";
export const secondaryButtonClassName =
  "inline-flex min-h-[46px] items-center justify-center gap-2 rounded-md border border-gray-200 bg-white px-6 text-[15px] font-extrabold text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60";
export const dangerButtonClassName =
  "inline-flex min-h-[46px] items-center justify-center gap-2 rounded-md border border-red-500 bg-red-500 px-6 text-[15px] font-extrabold text-white transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-60";
export const successButtonClassName =
  "inline-flex min-h-[46px] items-center justify-center gap-2 rounded-md border border-emerald-600 bg-emerald-600 px-6 text-[15px] font-extrabold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";

export const defaultAudienceFilters: AudienceFilters =
  buildDefaultNewsletterMemberFilters();

export const memberSizeOptions = [
  { id: "individual", label: "Individual" },
  { id: "2-9", label: "2-9 employees" },
  { id: "10-19", label: "10-19 employees" },
  { id: "20-29", label: "20-29 employees" },
  { id: "30-49", label: "30-49 employees" },
  { id: "50-99", label: "50-99 employees" },
  { id: "100-199", label: "100-199 employees" },
  { id: "200-499", label: "200-499 employees" },
  { id: "500-999", label: "500-999 employees" },
  { id: "1000-1999", label: "1000-1999 employees" },
  { id: "2000+", label: "2000+ employees" },
];
export const memberSinceOptions = [
  { id: "<1", label: "<1 year" },
  { id: "1-3", label: "1-3 years" },
  { id: "4-9", label: "4-9 years" },
  { id: "10-19", label: "10-19 years" },
  { id: "20-29", label: "20-29 years" },
  { id: "30-39", label: "30-39 years" },
  { id: "40-49", label: "40-49 years" },
  { id: "50+", label: "50+ years" },
];
export const renewalStatusOptions = [
  { id: "<30", label: "Less than 30 days out" },
  { id: "31-45", label: "31-45 days out" },
  { id: "46-60", label: "46-60 days out" },
  { id: "61-90", label: "61-90 days out" },
  { id: "91+", label: "91+ days out" },
];

export const formatNumber = (value: number) => value.toLocaleString();

const getEmployeeCount = (member: EmailAudienceMemberOption) =>
  (member.fullTimeEmployees || 0) + (member.partTimeEmployees || 0);

const isWithinEmployeeRange = (rangeId: string, count: number) => {
  if (rangeId === "individual") return count <= 1;
  if (rangeId.endsWith("+")) return count >= Number(rangeId.replace("+", ""));
  const [from, to] = rangeId.split("-").map(Number);
  return count >= from && count <= to;
};

const getYearsSince = (dateUtcMilli?: number | null) =>
  dateUtcMilli
    ? (Date.now() - dateUtcMilli) / (365.25 * 24 * 60 * 60 * 1000)
    : null;

const isWithinYearRange = (rangeId: string, years: number | null) => {
  if (years === null) return false;
  if (rangeId === "<1") return years < 1;
  if (rangeId.endsWith("+")) return years >= Number(rangeId.replace("+", ""));
  const [from, to] = rangeId.split("-").map(Number);
  return years >= from && years < to + 1;
};

const getDaysUntil = (dateUtcMilli?: number | null) =>
  dateUtcMilli
    ? Math.ceil((dateUtcMilli - Date.now()) / (24 * 60 * 60 * 1000))
    : null;

export const isWithinRenewalRange = (rangeId: string, days: number | null) => {
  if (days === null) return false;
  if (rangeId === "<30") return days >= 0 && days < 30;
  if (rangeId === "91+") return days >= 91;
  const [from, to] = rangeId.split("-").map(Number);
  return days >= from && days <= to;
};

export const getFilteredMemberIds = ({
  filters,
  memberOptions,
}: {
  filters: AudienceFilters;
  memberOptions: EmailAudienceMemberOption[];
}) => {
  return memberOptions
    .filter((member) => {
      const memberTypeMatch =
        filters.membershipTypeIds.length === 0 ||
        filters.membershipTypeIds.includes(member.membershipTypeId);
      const memberSizeMatch =
        filters.employeeRangeIds.length === 0 ||
        filters.employeeRangeIds.some((rangeId) =>
          isWithinEmployeeRange(rangeId, getEmployeeCount(member))
        );
      const industryMatch =
        filters.categoryIds.length === 0 ||
        filters.categoryIds.some((categoryId) =>
          (member.categoryId || []).includes(categoryId)
        );
      const memberSinceMatch =
        filters.memberSinceRangeIds.length === 0 ||
        filters.memberSinceRangeIds.some((rangeId) =>
          isWithinYearRange(rangeId, getYearsSince(member.dateJoinedUtcMilli))
        );
      const renewalMatch =
        filters.renewalRangeIds.length === 0 ||
        filters.renewalRangeIds.some((rangeId) =>
          isWithinRenewalRange(rangeId, getDaysUntil(member.renewalDateUtcMilli))
        );

      return (
        memberTypeMatch &&
        memberSizeMatch &&
        industryMatch &&
        memberSinceMatch &&
        renewalMatch
      );
    })
    .map((member) => member.id);
};

export const memberMatchesRecipientSource = ({
  member,
  sourceId,
}: {
  member: EmailAudienceMemberOption;
  sourceId: RecipientSourceId;
}): boolean => {
  if (sourceId === "allMembers") {
    return member.lifecycleStatus !== "DELETED";
  }
  if (sourceId === "activeMembers") {
    return member.lifecycleStatus === "ACTIVE";
  }
  if (sourceId === "newMembers") {
    const nowUtcMilli = Date.now();
    return (
      member.lifecycleStatus === "ACTIVE" &&
      !!member.dateJoinedUtcMilli &&
      member.dateJoinedUtcMilli <= nowUtcMilli &&
      member.dateJoinedUtcMilli >=
        nowUtcMilli - 60 * 24 * 60 * 60 * 1000
    );
  }
  if (sourceId === "renewingMembers") {
    const daysUntilRenewal = getDaysUntil(member.renewalDateUtcMilli);
    return (
      member.lifecycleStatus === "ACTIVE" &&
      daysUntilRenewal !== null &&
      daysUntilRenewal >= 0 &&
      daysUntilRenewal <= 60
    );
  }
  // Prospects are resolved from the dedicated prospect module, not from
  // ChamberMember lifecycle rows. Only the backend preview can count them.
  return false;
};

// Step 4 Audience Summary rows: each Step-1 selection becomes one bordered row
// with its own real count. `count: null` means the total lives on the backend
// (for example website subscribers) and the row renders an em dash instead of
// a made-up number.
export type AudienceSummaryRow = {
  id: string;
  label: string;
  count: number | null;
};

const backendOwnedRecipientCountSourceIds = new Set<RecipientSourceId>([
  "allMembers",
  "nonMemberSubscribers",
  "prospectMembers",
  "eventAttendees",
  "pastEventAttendees",
  "eblastSubscribers",
]);

export const recipientCountRequiresBackendPreview = (
  sourceIds: RecipientSourceId[]
): boolean =>
  sourceIds.some((sourceId) =>
    backendOwnedRecipientCountSourceIds.has(sourceId)
  );

export const resolveIntendedRecipientCount = ({
  fallbackCount,
  hasRecipients,
  isSentRecord,
  previewCount,
  requiresBackendPreview,
  storedCount,
}: {
  fallbackCount: number;
  hasRecipients: boolean;
  isSentRecord: boolean;
  previewCount: number | null | undefined;
  requiresBackendPreview: boolean;
  storedCount: number;
}): number | null => {
  if (!hasRecipients) return 0;
  if (isSentRecord) return storedCount;
  // A successful backend preview of zero is authoritative.
  if (previewCount !== null && previewCount !== undefined) return previewCount;
  if (requiresBackendPreview) return null;
  return fallbackCount;
};

const recipientSourceSummaryLabels: Partial<Record<RecipientSourceId, string>> = {
  allMembers: "Full Email Subscribers Lists",
  activeMembers: "Active Members",
  nonMemberSubscribers: nonMemberSubscribersRecipientSourceLabel,
  newMembers: "New Members (within 60 days)",
  renewingMembers: "Renewing Members (within 60 days)",
  prospectMembers: "Prospect Members",
  eblastSubscribers: "E-Blast Subscribers",
  eventAttendees: "Event Attendees",
  pastEventAttendees: "Past Event Attendees",
};

const audienceFilterSummaryPrefixes: Record<AudienceFilterId, string> = {
  membershipTypeIds: "Membership Type",
  employeeRangeIds: "Member Size",
  categoryIds: "Industry",
  memberSinceRangeIds: "Member Since",
  renewalRangeIds: "Renewal Status",
};

const prettifyEnumLabel = (value: string) =>
  value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const countMembersForRecipientSource = (
  sourceId: RecipientSourceId,
  memberOptions: EmailAudienceMemberOption[]
) =>
  memberOptions.filter((member) =>
    memberMatchesRecipientSource({ member, sourceId })
  ).length;

const countMembersForFilterValue = (
  filterId: AudienceFilterId,
  valueId: string,
  memberOptions: EmailAudienceMemberOption[]
) =>
  memberOptions.filter((member) => {
    if (filterId === "membershipTypeIds")
      return member.membershipTypeId === valueId;
    if (filterId === "employeeRangeIds")
      return isWithinEmployeeRange(valueId, getEmployeeCount(member));
    if (filterId === "categoryIds")
      return (member.categoryId || []).includes(valueId);
    if (filterId === "memberSinceRangeIds")
      return isWithinYearRange(valueId, getYearsSince(member.dateJoinedUtcMilli));
    return isWithinRenewalRange(valueId, getDaysUntil(member.renewalDateUtcMilli));
  }).length;

const getFilterValueLabel = (
  filterId: AudienceFilterId,
  valueId: string,
  industryLabels: Record<string, string>
) => {
  if (filterId === "membershipTypeIds")
    return industryLabels[`membershipType:${valueId}`] || valueId;
  if (filterId === "employeeRangeIds")
    return (
      memberSizeOptions.find((option) => option.id === valueId)?.label || valueId
    );
  if (filterId === "categoryIds")
    return industryLabels[valueId] || prettifyEnumLabel(valueId);
  if (filterId === "memberSinceRangeIds")
    return (
      memberSinceOptions.find((option) => option.id === valueId)?.label || valueId
    );
  return (
    renewalStatusOptions.find((option) => option.id === valueId)?.label || valueId
  );
};

export const buildAudienceSummaryRows = ({
  email,
  recipientGroups,
  memberOptions,
  audienceFilters,
  industryLabels = {},
}: {
  email: EmailFormState;
  recipientGroups: RecipientGroup[];
  memberOptions: EmailAudienceMemberOption[];
  audienceFilters: AudienceFilters;
  industryLabels?: Record<string, string>;
}): AudienceSummaryRow[] => {
  const rows: AudienceSummaryRow[] = [];

  email.recipientSourceIds.forEach((sourceId) => {
    const label = recipientSourceSummaryLabels[sourceId];
    if (!label) return;
    if (sourceId === "activeMembers") {
      rows.push({
        id: `source-${sourceId}`,
        label,
        count: memberOptions.filter(
          (member) => member.lifecycleStatus === "ACTIVE"
        ).length,
      });
      return;
    }
    if (sourceId === "allMembers") {
      rows.push({
        id: `source-${sourceId}`,
        label,
        // Full Email also includes current nonmember website subscribers,
        // which are intentionally not loaded into the dashboard member query.
        count: null,
      });
      return;
    }
    if (sourceId === "prospectMembers") {
      rows.push({
        id: `source-${sourceId}`,
        label,
        // Prospects live in their own module and are counted by backend preview.
        count: null,
      });
      return;
    }
    if (sourceId === "newMembers" || sourceId === "renewingMembers") {
      rows.push({
        id: `source-${sourceId}`,
        label,
        count: countMembersForRecipientSource(sourceId, memberOptions),
      });
      return;
    }
    rows.push({ id: `source-${sourceId}`, label, count: null });
  });

  recipientGroups
    .filter((group) => email.recipientGroupIds.includes(group.id))
    .forEach((group) => {
      rows.push({ id: `group-${group.id}`, label: group.name, count: group.count });
    });

  (Object.keys(audienceFilters) as AudienceFilterId[]).forEach((filterId) => {
    audienceFilters[filterId].forEach((valueId) => {
      rows.push({
        id: `filter-${filterId}-${valueId}`,
        label: `${audienceFilterSummaryPrefixes[filterId]}: ${getFilterValueLabel(
          filterId,
          valueId,
          industryLabels
        )}`,
        count: countMembersForFilterValue(filterId, valueId, memberOptions),
      });
    });
  });

  const hasFilterSelections = Object.values(audienceFilters).some(
    (values) => values.length > 0
  );
  if (
    !hasFilterSelections &&
    email.recipientSourceIds.includes("selectedMembers") &&
    email.selectedMemberIds.length > 0
  ) {
    rows.push({
      id: "source-selectedMembers",
      label: "Selected Members",
      count: email.selectedMemberIds.length,
    });
  }

  return rows;
};

export const SectionHeading = ({
  index,
  title,
  description,
}: {
  index: number;
  title: React.ReactNode;
  description?: string;
}) => (
  <div className="mb-6 flex items-start gap-3">
    <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cpaGold text-[15px] font-extrabold text-white">
      {index}
    </span>
    <div>
      <h2 className={sectionTitleClassName}>{title}</h2>
      {description ? (
        <p className={sectionDescriptionClassName}>{description}</p>
      ) : null}
    </div>
  </div>
);

export const Field = ({
  label,
  helper,
  required,
  children,
}: {
  label: string;
  helper?: React.ReactNode;
  required?: boolean;
  children: React.ReactNode;
}) => (
  <label className="block">
    <span className={fieldLabelClassName}>
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </span>
    {helper ? (
      <span className={fieldHelperClassName}>{helper}</span>
    ) : null}
    <div className="mt-3">{children}</div>
  </label>
);

export const MetricCard = ({ label, value }: { label: string; value: string }) => (
  <div className="rounded-xl border border-gray-200 bg-white px-6 py-5">
    <p className="text-[13px] font-extrabold leading-5 text-gray-950">{label}</p>
    <p className="mt-3 text-[28px] font-extrabold leading-none text-black">
      {value}
    </p>
  </div>
);

export const FilterDropdown = ({
  label,
  placeholder,
  options,
  values,
  onChange,
}: {
  label: string;
  placeholder: string;
  options: Array<{ id: string; label: string }>;
  values: string[];
  onChange: (values: string[]) => void;
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selectedLabels = options
    .filter((option) => values.includes(option.id))
    .map((option) => option.label);

  // Shared dropdown behavior (SH-005): close on outside click and Escape like
  // every other anchored menu in the dashboard.
  useEffect(() => {
    if (!open) {
      return;
    }
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const toggleValue = (value: string) => {
    onChange(
      values.includes(value)
        ? values.filter((currentValue) => currentValue !== value)
        : [...values, value]
    );
  };

  const setAll = () =>
    onChange(values.length === options.length ? [] : options.map((option) => option.id));

  return (
    <div className="relative" ref={containerRef}>
      <p className="mb-2.5 text-[14px] font-extrabold uppercase tracking-wide text-gray-500">
        {label}
      </p>
      <button
        type="button"
        className={`${inputClassName} flex items-center justify-between gap-2 text-left`}
        onClick={() => setOpen((currentOpen) => !currentOpen)}
      >
        <span
          className={`min-w-0 flex-1 truncate ${
            selectedLabels.length ? "text-gray-950" : "text-gray-500"
          }`}
        >
          {selectedLabels.length === 0
            ? placeholder
            : selectedLabels.length === 1
              ? selectedLabels[0]
              : `${selectedLabels.length} selected`}
        </span>
        <KeyboardArrowDownIcon
          className={`shrink-0 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open ? (
        <div className="absolute z-30 mt-2 max-h-80 w-full overflow-y-auto rounded-md border border-gray-200 bg-white p-4 shadow-xl">
          <label className="flex cursor-pointer items-center gap-3 py-2 text-[15px] text-black">
            <input
              type="checkbox"
              className={checkboxClassName}
              checked={values.length === options.length && options.length > 0}
              onChange={setAll}
            />
            Select All
          </label>
          {options.map((option) => (
            <label
              key={option.id}
              className="flex cursor-pointer items-center gap-3 py-2 text-[15px] text-black"
            >
              <input
                type="checkbox"
                className={checkboxClassName}
                checked={values.includes(option.id)}
                onChange={() => toggleValue(option.id)}
              />
              {option.label}
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
};
